import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ChevronLeft, Briefcase, Building, Percent, Calendar, User, Mail,
  Phone as PhoneIcon, MapPin, FileText, CheckSquare, AlertTriangle,
} from 'lucide-react';
import useApiMethods from '../../lib/useApiMethods';
import { cn } from '../../utils/tailwindUtils';


const STATUS_BADGE = {
  active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  paused: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  expired: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  inactive: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

const fmt = (iso) => iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

function daysUntil(iso) {
  if (!iso) return null;
  return Math.ceil((new Date(iso) - new Date()) / 86400000);
}


export default function CorporateAccountDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetch: apiFetch } = useApiMethods();
  const [account, setAccount] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [aResp, eResp] = await Promise.all([
          apiFetch(`engage/corporate-accounts/${id}/`),
          apiFetch('engage/enquiries/'),
        ]);
        if (cancelled) return;
        setAccount(aResp?.data || aResp?.data?.data || null);
        // Client-side filter: enquiries where source_details.corporate_account_id === id
        const all = eResp?.results || eResp?.data?.results || [];
        setEnquiries(all.filter(e => e.source === 'corporate_account' && String(e.source_details?.corporate_account_id) === String(id)));
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || 'Failed to load account');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const daysToExpiry = daysUntil(account?.contract_end_date);
  const covered = useMemo(() => Array.isArray(account?.covered_services) ? account.covered_services : [], [account]);

  if (loading && !account) return <div className="p-8 text-center text-gray-500 dark:text-gray-400">Loading…</div>;
  if (!account) return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300">{error || 'Account not found.'}</div>
      <button onClick={() => navigate('/engage/corporate-accounts')} className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm"><ChevronLeft className="w-3.5 h-3.5" /> Back</button>
    </div>
  );

  return (
    <div className="p-4 max-w-[1400px] mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <button onClick={() => navigate('/engage/corporate-accounts')} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
          <ChevronLeft className="w-4 h-4" /> Back to Corporate Accounts
        </button>
        <button onClick={() => navigate(`/engage/corporate-accounts/${id}/edit`)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm hover:bg-gray-50 dark:hover:bg-gray-700">Edit</button>
      </div>

      <div className="bg-gradient-to-r from-teal-500/5 to-emerald-500/5 dark:from-teal-500/10 dark:to-emerald-500/10 rounded-xl p-4 border border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center text-white shadow-sm">
            <Building className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">{account.company_name}</h1>
            <div className="flex items-center gap-2 mt-0.5 text-sm text-gray-600 dark:text-gray-400">
              <span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium', STATUS_BADGE[account.status] || STATUS_BADGE.inactive)}>{account.status}</span>
              {daysToExpiry !== null && daysToExpiry >= 0 && daysToExpiry <= 90 && (
                <span className="inline-flex items-center gap-1 text-[11px] font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 px-2 py-0.5 rounded-full">
                  <AlertTriangle className="w-3 h-3" /> Contract expires in {daysToExpiry} days
                </span>
              )}
              {account.gstin && <span className="text-[11px]">GSTIN: {account.gstin}</span>}
            </div>
          </div>
        </div>
      </div>

      {error && <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">{error}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Contract card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700">
          <h3 className="text-xs font-semibold text-gray-900 dark:text-white mb-2">Contract</h3>
          <div className="space-y-1.5 text-sm">
            <div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5 text-gray-400" />Start: {fmt(account.contract_start_date)}</div>
            <div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5 text-gray-400" />End: {fmt(account.contract_end_date)}</div>
            <div className="flex items-center gap-2"><Percent className="w-3.5 h-3.5 text-gray-400" />{account.discount_pct}% discount</div>
            {account.billing_terms && <div className="flex items-center gap-2"><FileText className="w-3.5 h-3.5 text-gray-400" />{account.billing_terms}</div>}
          </div>
        </div>

        {/* Contact card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700">
          <h3 className="text-xs font-semibold text-gray-900 dark:text-white mb-2">Primary contact</h3>
          <div className="space-y-1.5 text-sm">
            {account.primary_contact_name && <div className="flex items-center gap-2"><User className="w-3.5 h-3.5 text-gray-400" />{account.primary_contact_name}</div>}
            {account.primary_contact_mobile && <div className="flex items-center gap-2"><PhoneIcon className="w-3.5 h-3.5 text-gray-400" />{account.primary_contact_mobile}</div>}
            {account.primary_contact_email && <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-gray-400" />{account.primary_contact_email}</div>}
            {account.registered_address && <div className="flex items-start gap-2"><MapPin className="w-3.5 h-3.5 text-gray-400 mt-0.5" /><span className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{account.registered_address}</span></div>}
          </div>
        </div>

        {/* Covered services */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700">
          <h3 className="text-xs font-semibold text-gray-900 dark:text-white mb-2">Covered services</h3>
          {covered.length === 0 ? (
            <div className="text-sm text-gray-500 dark:text-gray-400">No services configured.</div>
          ) : (
            <div className="flex flex-wrap gap-1">
              {covered.map((s, i) => (
                <span key={i} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300">
                  <CheckSquare className="w-3 h-3" /> {s}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Enquiries from this corporate */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700"><h3 className="text-sm font-semibold text-gray-900 dark:text-white">Enquiries from this account ({enquiries.length})</h3></div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/40 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <tr>
              <th className="px-4 py-2 text-left font-medium">Employee</th>
              <th className="px-4 py-2 text-left font-medium">Department</th>
              <th className="px-4 py-2 text-left font-medium">Stage</th>
              <th className="px-4 py-2 text-left font-medium">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {enquiries.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">No enquiries from this account yet.</td></tr>
            ) : enquiries.map(e => (
              <tr key={e.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                <td className="px-4 py-2.5"><Link to={`/engage/enquiries/${e.id}`} className="font-medium text-blue-600 dark:text-blue-400 hover:underline">{e.name}</Link></td>
                <td className="px-4 py-2.5 text-[12px] text-gray-700 dark:text-gray-300">{e.department_of_interest || '—'}</td>
                <td className="px-4 py-2.5 text-[12px] text-gray-700 dark:text-gray-300">{e.status}</td>
                <td className="px-4 py-2.5 text-[12px] text-gray-500 dark:text-gray-400">{fmt(e.created_date)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
